import type { ReactNode } from 'react'
import { QuickAccess } from './QuickAccess'
import { useEffect, useState } from 'react'
import { Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  Compass,
  LogOut,
  Mail,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Shirt,
  Sun,
  Globe,
  Languages,
  UserRound,
  UsersRound,
  Sparkles,
  Gamepad2,
  MessageSquareText,
  Search,
  Keyboard,
  ScrollText,
  Settings,
  CircleHelp,
} from 'lucide-react'
import { redock } from '../lib/dockPlacement'
import { Dropdown, DropdownItem } from './Dropdown'
import { useInboxNotifications } from '../lib/inboxNotify'
import { isMainWindow } from '../lib/appWindows'
import { api } from '../lib/api'
import { atLeast, clearSession, getSession } from '../lib/auth'
import { useI18n, LOCALES, LOCALE_LABELS } from '../lib/i18n'
import { useTheme } from '../lib/theme'
import { realtime, type ConnectionState } from '../lib/realtime'
import { Avatar, Badge, LiveDot } from './primitives'
import Logo from './Logo'
import { SceneryProvider } from '../bughunt/SceneryProvider'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { usePersistedFlag } from '../lib/usePersistedFlag'
import { versionLabel } from '../lib/version'
import { profileOpen, profilesList } from '../lib/profiles'
import { OtherAccountDialog } from './OtherAccountDialog'
import { MenuBar } from './MenuBar'
import { FreeProject } from './FreeProject'
import { kidsAllowed } from '../lib/kids'
import { PromptDock } from './PromptDock'
import { CommandPalette } from './CommandPalette'
import { openPalette } from '../lib/palette'
import { FirstRunLayer } from './FirstRunGuide'
import { openFirstRunGuide } from '../lib/firstRunGuide'
import { isDesktop, openExternal, siteUrl } from '../lib/desktop'
import { RunBar } from './RunBar'
import { SensitiveStep } from './SensitiveStep'
import { useFirstAgentAccounts } from './AgentAccountsHint'
import { HomePcStatus } from './HomePcStatus'
import { ProjectsSidebar } from './ProjectsSidebar'
import { UpdateNotice } from './UpdateNotice'
import { OutboxNotice } from './OutboxNotice'
import { useSimpleMode } from '../lib/simpleMode'
import { useFused } from '../lib/fuse'
import { telemetryOutbox } from '../lib/runnerHost'
import { NAV_ICONS, areaPaths, titleKeyFor, visibleAreas, type NavArea, type NavItem as NavItemDef } from '../lib/navigation'

export default function Shell({ children }: { children: ReactNode }) {
  const { t, locale, setLocale } = useI18n()
  const [theme, setTheme] = useTheme()
  // On the desktop: starts the reports' outbox once, which also sends what an earlier run of the app left (docs/refactor/52).
  const [outboxReporter] = useState(() => telemetryOutbox())
  const location = useLocation()
  const navigate = useNavigate()
  const session = getSession()

  // The menu folds to icons for more room, and stays folded.
  const [navCollapsed, toggleNav] = usePersistedFlag('devpulse:nav-collapsed', false)
  // Ctrl+B folds and unfolds it, as in VS Code.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        toggleNav()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleNav])

  const [connection, setConnection] = useState<ConnectionState>(realtime.connectionState)
  // "Abrir outra conta em nova janela" (docs/refactor/45).
  const [otherAccount, setOtherAccount] = useState(false)
  useEffect(() => realtime.onStateChange(setConnection), [])

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: api.auth.me,
    staleTime: 5 * 60_000,
  })

  const { data: bugCounts } = useQuery({
    queryKey: ['bug-hunt', 'counts'],
    queryFn: () => api.bugHunt.state(),
    staleTime: 60_000,
    enabled: me?.bugHuntEnabled !== false,
  })

  const canManage = atLeast(session?.role, 'EngineeringManager')

  // Open high-severity findings, as a count on the Security item. Managers only; polled lightly.
  const { data: securitySummary } = useQuery({
    queryKey: ['security', 'summary'],
    queryFn: api.security.summary,
    enabled: canManage,
    refetchInterval: 60_000,
    retry: false,
  })
  const bugHuntEnabled = me?.bugHuntEnabled !== false

  // The tree is the same for the sidebar and the menu bar: one place decides what each person sees.
  const areas = visibleAreas({ role: session?.role, isDeveloper: Boolean(me?.developerId), bugHuntEnabled })
  const settingsArea = areas.find((area) => area.placement === 'header') ?? null
  // The inbox's unread count, on its item; the notifications for new ones (lib/inboxNotify.ts).
  // Asked again the moment the server says it changed; the round every minute is only a fallback.
  const { data: inbox } = useQuery({ queryKey: ['inbox'], queryFn: api.inbox.list, enabled: Boolean(me?.developerId), refetchInterval: 60_000, retry: false })
  const inboxClient = useQueryClient()
  useEffect(() => realtime.onInboxChanged(() => void inboxClient.invalidateQueries({ queryKey: ['inbox'] })), [inboxClient])
  // Told once per computer, by the main window (lib/appWindows.ts).
  useInboxNotifications(isMainWindow() ? inbox : undefined)
  // First time on the desktop with no agent ready: open on Conectar agente (docs/refactor/22).
  useFirstAgentAccounts()
  const badges: Record<string, string | undefined> = {
    inbox: inbox?.unread ? String(inbox.unread) : undefined,
    bugHunt: bugCounts?.counts.inProgress ? String(bugCounts.counts.inProgress) : undefined,
    security: securitySummary?.unreviewedHigh ? String(securitySummary.unreviewedHigh) : undefined,
  }

  /*
    "Connect an agent" stays at the top of the sidebar only until the person's agent has spoken to
    us. The signal is one of their own keys having been used (`lastUsedAt` set, not revoked): it is
    what the connect screen itself shows as "connected", it is a single cheap call shared with that
    screen's cache, and it does not depend on what the organization's other people are doing.
    Until the answer arrives, or if it fails, the button shows — hiding the way in is the worse error.
  */
  const { data: myKeys, isSuccess: keysLoaded } = useQuery({
    queryKey: ['me', 'credentials'],
    queryFn: api.me.credentials,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const agentConnected = keysLoaded && (myKeys ?? []).some((k) => !k.revokedAt && Boolean(k.lastUsedAt))

  const title = (() => {
    const path = location.pathname
    // The account is everyone's, reached from the avatar; the rest of /settings is the organization's.
    if (path.startsWith('/settings/account')) return t('settings.account')
    const key = titleKeyFor(path)
    if (key) return t(key)
    // Screens outside the tree: opened from somewhere inside it.
    if (path.startsWith('/work')) return t('work.title')
    if (path.startsWith('/developers')) return t('day.title')
    if (path.startsWith('/me/character')) return t('character.title')
    if (path.startsWith('/contact')) return t('contact.title')
    if (path.startsWith('/terms')) return t('terms.title')
    if (path.startsWith('/onboarding')) return t('nav.connectAgent')
    return ''
  })()

  // Kids mode (docs/refactor/48): the rooms, the start ideas, the character — and the rest out of the way.
  const kids = me?.kidsMode === true
  // Modo simples (docs/refactor/51, N5): no menu bar, no quick switches — the web's two menus instead.
  const simple = useSimpleMode() && !kids
  // "Fundir" (docs/refactor/62): only the prompt box stays; the rest of the IDE waits behind "Restaurar".
  const fused = useFused()

  const connectionLabel =
    connection === 'connected' ? t('common.live')
      : connection === 'reconnecting' || connection === 'connecting' ? t('common.reconnecting')
        : t('common.offline')

  return (
    <div className={`app${navCollapsed ? ' app--nav-collapsed' : ''}${fused ? ' app--fused' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar__brand">
          <Logo size={26} />
          <span className="sidebar__brand-name">Vephus</span>
          {/* Folding the menu, in the menu itself (owner, 10/10/2026); Ctrl+B too. */}
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm sidebar__collapse"
            onClick={toggleNav}
            aria-pressed={navCollapsed}
            title={`${navCollapsed ? t('nav.expand') : t('nav.collapse')} (Ctrl+B)`}
            aria-label={navCollapsed ? t('nav.expand') : t('nav.collapse')}
          >
            {navCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {/* Acesso rápido: commands from elsewhere (the base of Vephus) and teammates' work, on by default, one click
            away, at the top where the prompt box never covers them (components/QuickAccess.tsx). */}
        {!simple && <QuickAccess />}

        <nav className="sidebar__nav">
          {kids ? (
            <>
              <NavItem to="/kids" icon={<Sparkles size={16} />} label={t('kids.nav.start')} end />
              <NavItem to="/track/floor" icon={<Gamepad2 size={16} />} label={t('kids.nav.rooms')} />
              <NavItem to="/me/character" icon={<Shirt size={16} />} label={t('nav.character')} />
              <ProjectsSidebar />
            </>
          ) : (
          <>
          {/* Connecting an agent is what everything else depends on: first, and hard to miss — until it is done. */}
          {!agentConnected && <NavItem to="/agents/connect" icon={<Plug size={16} />} label={t('nav.onboarding')} cta />}

          {/* Trabalho: the projects open on this machine (desktop), above the app's own areas. */}
          <ProjectsSidebar />

          {areas.filter((area) => area.placement !== 'header').map((area) =>
            area.to ? (
              <NavItem key={area.id} to={area.to} icon={<AreaIcon area={area} />} label={t(area.labelKey)} badge={badges[area.id]} />
            ) : (
              <NavGroup key={area.id} id={area.id} label={t(area.labelKey)} icon={<AreaIcon area={area} />} paths={areaPaths(area)}>
                {area.items.map((item) => (
                  <TreeItem key={item.id} item={item} label={t(item.labelKey)} badge={badges[item.id]} />
                ))}
              </NavGroup>
            ),
          )}
          </>
          )}
        </nav>

        <div className="sidebar__footer">
          {/* Only drawn for somebody in more than one organization. */}
          <OrganizationSwitcher />
        </div>
        {/* The version, always at the sidebar's foot (the owner, 16/10) — and still in Ajuda as before. */}
        <div className="sidebar__version tiny muted">{versionLabel()}</div>
      </aside>


      <header className="topbar">
        {/* The desktop app: an IDE's menu bar, Arquivo to Ajuda, gathering what was scattered (docs/refactor/46). */}
        {kids ? null : isDesktop() && !simple ? (
          <MenuBar
            settingsItems={(settingsArea?.items ?? []).map((item) => ({ id: item.id, label: t(item.labelKey), to: item.to }))}
            onToggleNav={toggleNav}
            theme={theme}
            onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            onOtherAccount={() => setOtherAccount(true)}
          />
        ) : (
        // The web: Configurações and Ajuda, what is set up once, out of the sidebar.
        <nav className="topbar__menus" aria-label={t('nav.appMenus')}>
          {settingsArea && (
            <Dropdown label={t('nav.settings')} align="start" buttonClassName="btn btn--ghost btn--sm topbar__menu" button={<><Settings size={15} aria-hidden /><span className="topbar__menu-label">{t('nav.settings')}</span><ChevronDown size={12} className="topbar__menu-chevron" /></>}>
              {(close) => (
                <>
                  {settingsArea.items.map((item) => {
                    const Icon = NAV_ICONS[item.icon]
                    return <DropdownItem key={item.id} icon={<Icon size={14} />} label={t(item.labelKey)} onSelect={() => { close(); navigate(item.to) }} />
                  })}
                  <div className="dropdown__sep" role="separator" />
                  <DropdownItem icon={<UserRound size={14} />} label={t('settings.account')} onSelect={() => { close(); navigate('/settings/account') }} />
                  <div className="dropdown__row">
                    <Languages size={14} aria-hidden />
                    <select className="select select--sm" value={locale} onChange={(event) => setLocale(event.target.value as typeof locale)} aria-label={t('nav.language')}>
                      {LOCALES.map((code) => (
                        <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
                      ))}
                    </select>
                  </div>
                  <DropdownItem
                    icon={theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                    label={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
                    onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  />
                </>
              )}
            </Dropdown>
          )}
          <Dropdown label={t('nav.help')} align="start" buttonClassName="btn btn--ghost btn--sm topbar__menu" button={<><CircleHelp size={15} aria-hidden /><span className="topbar__menu-label">{t('nav.help')}</span><ChevronDown size={12} className="topbar__menu-chevron" /></>}>
            {(close) => (
              <>
                <DropdownItem icon={<Compass size={14} />} label={t('guide.reopen')} onSelect={() => { close(); openFirstRunGuide() }} />
                <DropdownItem icon={<Keyboard size={14} />} label={t('nav.shortcuts')} hint={t('nav.shortcutsHint')} onSelect={() => { close(); openPalette() }} />
                <DropdownItem icon={<Mail size={14} />} label={t('contact.title')} onSelect={() => { close(); navigate('/contact') }} />
                <DropdownItem icon={<ScrollText size={14} />} label={t('terms.title')} onSelect={() => { close(); navigate('/terms') }} />
                <div className="dropdown__foot tiny muted">{versionLabel()}</div>
              </>
            )}
          </Dropdown>
        </nav>
        )}
        {/* A screen in the sidebar tree says where one is in the sidebar (and a tabbed space in its tab row): the
            header title would only repeat it, truncated by the pickers (41, A1). It stays for screens outside the
            tree — /work/:id, a developer's day — which have no other sign. */}
        {title && !location.pathname.startsWith('/settings') && !titleKeyFor(location.pathname) && <h1 className="topbar__title">{title}</h1>}
        {/* ▶ Play: the project in Docker, opened in the browser. */}
        <RunBar kids={kids} />
        {me?.isDemoOrganization && <Badge tone="demo">{t('common.demoData')}</Badge>}

        <div className="topbar__spacer" />

        {/* Go anywhere, do anything, from the keyboard: the same as Ctrl+K (docs/refactor/07-ctrl-k.md). */}
        {!kids && (
          <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={openPalette} title={t('palette.button')} aria-label={t('palette.button')}>
            <Search size={15} />
          </button>
        )}

        {/* Brings the prompt box back, docked along the bottom where it always opens (Ctrl+J too). */}
        <button type="button" className="btn btn--ghost btn--sm topbar__prompt" onClick={redock} title={t('nav.promptButtonHint')}>
          <MessageSquareText size={14} /> <span>{t('nav.promptButton')}</span>
        </button>

        {/* The web: is the IDE at home on? (docs/refactor/16-ide-em-casa.md) */}
        <HomePcStatus enabled={Boolean(me?.developerId)} />

        {/* The connection as a dot: its state in the hint, never words that crowd the bar. */}
        <span className="topbar__live" title={connectionLabel} aria-label={connectionLabel} role="status">
          <LiveDot state={connection === 'connected' ? 'live' : connection === 'disconnected' ? 'off' : 'stale'} />
        </span>

        {/* Everything about the person and the app's look, behind the avatar (docs/refactor/04-cabecalho.md). */}
        <Dropdown
          label={t('nav.userMenu')}
          className="topbar__user"
          buttonClassName="btn btn--ghost btn--icon topbar__avatar"
          button={<Avatar name={session?.displayName ?? '?'} size="sm" />}
        >
          {(close) => (
            <>
              <div className="dropdown__head">
                <span className="small truncate">{session?.displayName}</span>
                <span className="tiny muted truncate">{t(`role.${session?.role ?? 'Viewer'}`)}</span>
              </div>
              {!kids && <DropdownItem icon={<UserRound size={14} />} label={t('settings.account')} onSelect={() => { close(); navigate('/settings/account') }} />}
              {me?.developerId && <DropdownItem icon={<Shirt size={14} />} label={t('nav.character')} onSelect={() => { close(); navigate('/me/character') }} />}
              {isDesktop() && !kids && <DropdownItem icon={<UsersRound size={14} />} label={t('otherAccount.menu')} onSelect={() => { close(); setOtherAccount(true) }} />}
              {isDesktop() && !kids && <DropdownItem icon={<Globe size={14} />} label={t('nav.viewOnWeb')} hint={t('nav.viewOnWebHint')} onSelect={() => { close(); void viewOnWeb(location.pathname + location.search) }} />}
              <DropdownItem icon={<LogOut size={14} />} label={t('nav.signOut')} danger onSelect={clearSession} />
            </>
          )}
        </Dropdown>
      </header>

      {otherAccount && <OtherAccountDialog list={profilesList} open={profileOpen} onClose={() => setOtherAccount(false)} />}
      {/* The free project, "Livre": nobody is stuck without a project (docs/refactor/49). */}
      <FreeProject />
      {/* The quiet notices at the foot of the menu: a newer version, reports still waiting to reach Vephus (docs/refactor/52). */}
      <div className="foot-notices">
        <UpdateNotice />
        <OutboxNotice reporter={outboxReporter} />
      </div>
      <main className={location.pathname.startsWith('/track/bugs') || location.pathname.startsWith('/track/floor') ? 'main main--flush' : 'main'}>
        {kids && !kidsAllowed(location.pathname) ? <Navigate to="/kids" replace /> : <SceneryProvider>{children}</SceneryProvider>}
      </main>

      {/* The prompt to one's own agent, on every screen. */}
      <PromptDock />

      {/* "O que só você decide": at a new project, an invitation sent or accepted (docs/refactor/18). */}
      <SensitiveStep />

      {/* Ctrl+K: go anywhere, do the everyday things, without the mouse. */}
      <CommandPalette />

      {/* The Terms of Use when they still need accepting, then the first-run guide, once. */}
      <FirstRunLayer />
    </div>
  )
}

/**
 * A folding section of the sidebar. Remembers whether it was open, opens by itself when the page
 * shown is one of its own, and in the narrow icon-only sidebar is not folded at all.
 */
function NavGroup({ id, label, icon, paths, children }: { id: string; label: string; icon: ReactNode; paths: string[]; children: ReactNode }) {
  const location = useLocation()
  const key = `devpulse:nav-${id}`
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) !== 'closed'
    } catch {
      return true
    }
  })
  const here = paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))

  useEffect(() => {
    if (here) setOpen(true)
  }, [here])

  const toggle = (): void => {
    const next = !open
    setOpen(next)
    try {
      localStorage.setItem(key, next ? 'open' : 'closed')
    } catch {
      // Remembering is a nicety.
    }
  }

  return (
    <div className={`nav-group${open ? ' nav-group--open' : ''}${here ? ' nav-group--here' : ''}`}>
      <button type="button" className="nav-group__header" aria-expanded={open} onClick={toggle}>
        {icon}
        <span>{label}</span>
        <ChevronDown size={14} className="nav-group__chevron" aria-hidden />
      </button>
      <div className="nav-group__items" role="group" aria-label={label}>
        {children}
      </div>
    </div>
  )
}

function AreaIcon({ area }: { area: NavArea }) {
  const Icon = NAV_ICONS[area.icon]
  return <Icon size={16} />
}

function TreeItem({ item, label, badge }: { item: NavItemDef; label: string; badge?: string }) {
  const Icon = NAV_ICONS[item.icon]
  return <NavItem to={item.to} end={item.end} icon={<Icon size={16} />} label={label} badge={badge} />
}

function NavItem({ to, icon, label, badge, cta = false, end = false }: { to: string; icon: ReactNode; label: string; badge?: string; cta?: boolean; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item${cta ? ' nav-item--cta' : ''}${isActive ? ' nav-item--active' : ''}`} title={label}>
      {icon}
      <span>{label}</span>
      {badge && <span className="nav-item__badge">{badge}</span>}
    </NavLink>
  )
}

/**
 * "View on the web", in the desktop app: opens this same screen in the browser, already signed in,
 * with a one-time code rather than the session itself in the address — after the `#`, which never leaves the browser.
 */
async function viewOnWeb(path: string) {
  const { code } = await api.auth.handoff()
  await openExternal(`${siteUrl()}/auth/handoff#code=${encodeURIComponent(code)}&next=${encodeURIComponent(path)}`)
}
